import React from 'react'
import { createRoot } from 'react-dom/client'
import VideoModal from './VideoModal.jsx'
import VideoProgressManager from './videoProgress.js'
import { initBodyScroll } from '../../common/overlayScrollbars.js'

const videoProgressManager = new VideoProgressManager()

// Global state for video modal
let globalVideoModalManager = null
let isInitialized = false

/**
 * True on TV single/archive body classes or on FSTV listing URL (/fstv/).
 * Archive pages use post-type-archive-tv, not post-type-tv.
 */
function isTvVideoContext() {
  if (typeof document === 'undefined' || !document.body) {
    return false
  }
  const body = document.body
  if (
    body.classList.contains('post-type-tv') ||
    body.classList.contains('post-type-archive-tv')
  ) {
    return true
  }
  const raw = window.location.pathname
  const path = raw.replace(/\/+$/, '') || '/'
  return path === '/fstv' || path.endsWith('/fstv') || raw.includes('/fstv/')
}

/**
 * Extract TV post slug from a /fstv/{slug}/ pathname.
 * Returns null for bare /fstv/ (the listing page).
 */
function extractTvSlug(pathname) {
  const match = pathname.match(/\/fstv\/([^/?#]+)\/?$/)
  return match ? match[1] : null
}

class VideoModalManager {
  constructor() {
    this.isOpen = false
    this.videoData = null
    this.root = null
    this.container = null
    this.currentPlaylist = null // Ordered youtube IDs from the source carousel
  }

  static getInstance() {
    if (!globalVideoModalManager) {
      globalVideoModalManager = new VideoModalManager()
      globalVideoModalManager.init()
    }
    return globalVideoModalManager
  }

  init() {
    if (isInitialized) {
      return
    }

    isInitialized = true

    const tvContext = isTvVideoContext()

    if (!tvContext) {
      // Strip legacy params from non-TV pages
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.has('video') || urlParams.has('v')) {
        const newUrl = new URL(window.location)
        newUrl.searchParams.delete('video')
        newUrl.searchParams.delete('v')
        newUrl.searchParams.delete('fullscreen')
        history.replaceState({}, '', newUrl)
      }
    } else {
      // Read slug from ?v= param (after redirect from /fstv/{slug}/)
      const urlParams = new URLSearchParams(window.location.search)
      const videoSlug = urlParams.get('v')

      if (videoSlug) {
        // Page load — no user gesture, don't autoplay
        this.openVideoBySlug(videoSlug, false, false, false).catch(() => {
          // Invalid slug — clean URL
          history.replaceState({}, '', '/fstv/')
        })
      }
    }

    // Initialize progress bars
    videoProgressManager.initializeProgressBars()

    window.addEventListener('popstate', () => {
      if (!isTvVideoContext()) {
        this.closeVideoModal()
        return
      }

      const videoSlug = extractTvSlug(window.location.pathname)
      const newFullscreen = false

      if (videoSlug) {
        // Check for fresh start flag
        const freshStart = !!window.__playNextFresh
        if (window.__playNextFresh) {
          delete window.__playNextFresh
        }

        this.openVideoBySlug(videoSlug, newFullscreen, freshStart).catch((error) => {
          console.warn('Failed to load TV modal on popstate:', error)
        })
      } else {
        this.closeVideoModal()
      }
    })

    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/fstv/"]')
      if (!link) return

      const url = new URL(link.href, window.location.origin)
      const slug = extractTvSlug(url.pathname)
      if (!slug) return // Just the /fstv/ listing page, not a video

      // Only intercept clicks when we're already on the /fstv/ page.
      // From other pages, let the link navigate — server redirect will load /fstv/
      // and JS will open the modal there.
      if (!isTvVideoContext()) return

      e.preventDefault()

      // Read flags from data attributes (preferred) or query params (legacy)
      let fullscreen = link.dataset.fullscreen === 'true' || url.searchParams.get('fullscreen') === 'true'
      const autoplay = link.dataset.autoplay !== 'false' && url.searchParams.get('autoplay') !== 'false'

      // Auto-fullscreen on mobile/touch devices for links with data-mobile-fullscreen
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      if (!fullscreen && link.hasAttribute('data-mobile-fullscreen') && isTouchDevice) {
        fullscreen = true
      }

      // Extract playlist from parent carousel's swiper data-playlist attribute
      const swiperEl = link.closest('.swiper[data-playlist]')
      if (swiperEl) {
        const playlistStr = swiperEl.getAttribute('data-playlist')
        if (playlistStr) {
          this.currentPlaylist = playlistStr.split(',').filter(Boolean)
        }
      }

      // Extract notification config from carousel
      const notifySwiperEl = swiperEl || link.closest('.swiper[data-notify]')
      if (notifySwiperEl && notifySwiperEl.dataset.notify === 'true') {
        this.notifyConfig = {
          enabled: true,
          heading: notifySwiperEl.dataset.notifyHeading || '',
          subheading: notifySwiperEl.dataset.notifySubheading || '',
        }
      } else {
        this.notifyConfig = null
      }

      // Check if this is a Play Next trigger (fresh start, skip resume)
      const freshStart = !!window.__playNextFresh
      if (window.__playNextFresh) {
        delete window.__playNextFresh
      }

      this.openVideoBySlug(slug, fullscreen, freshStart, autoplay).catch((error) => {
        console.warn('Failed to load TV modal:', error)
      })
    })
  }

  async openVideoBySlug(slug, fullscreen = false, freshStart = false, autoplay = true) {
    if (this.isOpen) {
      return
    }

    try {
      document.body.classList.add('is-loading')

      const ajaxParams = {
        action: 'load_video_modal',
        video_slug: slug
      }

      if (this.currentPlaylist && this.currentPlaylist.length > 0) {
        ajaxParams.playlist = this.currentPlaylist.join(',')
      }

      const response = await fetch(eyfs.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(ajaxParams)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success && data.data) {
        this.showModal(data.data, data.data.youtube_id, fullscreen, freshStart, autoplay)
      } else {
        throw new Error(data.data?.message || 'Failed to load video modal')
      }

      // Clean URL: /fstv/{slug}/
      history.replaceState({}, '', `/fstv/${slug}/`)

    } catch (error) {
      console.error('Error loading video modal:', error)
      throw error
    } finally {
      document.body.classList.remove('is-loading')
    }
  }

  showModal(videoData, videoId, fullscreen = false, freshStart = false, autoplay = true) {
    if (this.isOpen) {
      return
    }

    const existingModal = document.querySelector('.video-modal')
    if (existingModal) {
      existingModal.remove()
    }

    this.container = document.createElement('div')
    this.container.id = 'video-modal-root'
    document.body.appendChild(this.container)

    this.root = createRoot(this.container)

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      window.dispatchEvent(new CustomEvent('modalOpened'))
    }, 150)

    const processedVideoData = {
      youtube_id: videoId,
      video_source: videoData.video_source || 'youtube',
      vimeo_id: videoData.vimeo_id || '',
      video_file_url: videoData.video_file_url || '',
      featured_image_url: videoData.featured_image_url || '',
      slug: videoData.slug || '',
      date: videoData.date || '',
      title: videoData.title || '',
      description: videoData.description || '',
      topics: videoData.topics || [],
      speakers: videoData.speakers || [],
      transcript: videoData.transcript || null,
      transcript_vtt: videoData.transcript_vtt || null,
      channel_name: videoData.channel_name || (videoData.related_videos?.[0]?.channel) || '',
      related_videos: videoData.related_videos || [],
      notifyConfig: this.notifyConfig || null,
    }

    this.root.render(
      React.createElement(VideoModal, {
        isOpen: true,
        onClose: () => this.closeVideoModal(),
        videoData: processedVideoData,
        fullscreen: fullscreen,
        freshStart: freshStart,
        autoplay: autoplay
      })
    )

    this.isOpen = true
    this.videoData = processedVideoData
  }

  closeVideoModal() {
    if (!this.isOpen) {
      return;
    }

    initBodyScroll()

    if (this.currentVideoSelectedHandler) {
      document.removeEventListener('videoSelected', this.currentVideoSelectedHandler);
      this.currentVideoSelectedHandler = null;
    }

    if (this.root) {
      this.root.unmount()
      this.root = null
    }

    if (this.container) {
      this.container.remove()
      this.container = null
    }

    this.isOpen = false
    this.videoData = null

    // Reset URL to /fstv/ listing
    if (isTvVideoContext() && extractTvSlug(window.location.pathname)) {
      history.pushState({}, '', '/fstv/')
    }

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      window.dispatchEvent(new CustomEvent('modalClosed'))
    }, 100)
  }

  extractYouTubeId(url) {
    if (!url) return null
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/.*[?&]v=([^&\n?#]+)/
    ]
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) return match[1]
    }
    return null
  }

  extractVimeoId(url) {
    if (!url) return null
    const patterns = [
      /vimeo\.com\/(\d+)/,
      /vimeo\.com\/.*\/(\d+)/
    ]
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) return match[1]
    }
    return null
  }

  async openVideoModalFromUrl(url, fullscreen = false) {
    const youtubeId = this.extractYouTubeId(url)
    const vimeoId = this.extractVimeoId(url)

    if (youtubeId) {
      // Non-TV context: show minimal player for embedded YouTube links
      this.showModalMinimal(youtubeId, fullscreen)
    } else if (vimeoId) {
      this.showVimeoModal(vimeoId, fullscreen)
    }
  }

  showModalMinimal(youtubeId, fullscreen = false, updateUrl = false) {
    if (this.isOpen) {
      return
    }

    const existingModal = document.querySelector('.video-modal')
    if (existingModal) {
      existingModal.remove()
    }

    this.container = document.createElement('div')
    this.container.id = 'video-modal-root'
    document.body.appendChild(this.container)

    this.root = createRoot(this.container)

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      window.dispatchEvent(new CustomEvent('modalOpened'))
    }, 150)

    const processedVideoData = {
      youtube_id: youtubeId,
      date: '',
      title: '',
      description: '',
      topics: [],
      speakers: [],
      transcript: null,
      transcript_vtt: null,
      channel_name: '',
      related_videos: []
    }

    this.root.render(
      React.createElement(VideoModal, {
        isOpen: true,
        onClose: () => this.closeVideoModal(),
        videoData: processedVideoData,
        fullscreen: fullscreen
      })
    )

    this.isOpen = true
    this.videoData = processedVideoData
  }

  showVimeoModal(vimeoId, fullscreen = false) {
    if (this.isOpen) {
      return
    }

    const existingModal = document.querySelector('.video-modal')
    if (existingModal) {
      existingModal.remove()
    }

    this.container = document.createElement('div')
    this.container.id = 'video-modal-root'
    document.body.appendChild(this.container)

    this.root = createRoot(this.container)

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      window.dispatchEvent(new CustomEvent('modalOpened'))
    }, 150)

    // Dla Vimeo - prosty iframe (Vidstack ma problemy z Vimeo CC)
    const vimeoUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`

    const iframe = document.createElement('iframe')
    iframe.src = vimeoUrl
    iframe.allow = 'autoplay; fullscreen; picture-in-picture'
    iframe.style.width = '100%'
    iframe.style.height = '100%'
    iframe.style.border = 'none'

    const modalWrapper = document.createElement('div')
    modalWrapper.className = 'video-modal vimeo-modal'
    modalWrapper.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.95);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `

    const closeButton = document.createElement('button')
    closeButton.textContent = '×'
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      background: transparent;
      border: none;
      color: white;
      font-size: 40px;
      cursor: pointer;
      z-index: 1000000;
      width: 50px;
      height: 50px;
      line-height: 50px;
    `
    closeButton.onclick = () => this.closeVideoModal()

    const videoContainer = document.createElement('div')
    videoContainer.style.cssText = `
      width: 100%;
      max-width: 1400px;
      aspect-ratio: 16/9;
      position: relative;
    `

    videoContainer.appendChild(iframe)
    modalWrapper.appendChild(closeButton)
    modalWrapper.appendChild(videoContainer)
    this.container.appendChild(modalWrapper)

    this.isOpen = true
    this.videoData = { vimeo_id: vimeoId }

    const newUrl = new URL(window.location)
    newUrl.searchParams.set('video', vimeoId)
    if (fullscreen) {
      newUrl.searchParams.set('fullscreen', 'true')
    }
    history.pushState({}, '', newUrl)
  }

  destroy() {
    this.closeVideoModal()
    isInitialized = false
    globalVideoModalManager = null
  }
}

// Initialize on DOM ready with singleton pattern
document.addEventListener('DOMContentLoaded', () => {
  // Only create one instance and prevent multiple initializations
  if (!globalVideoModalManager && !isInitialized) {
    globalVideoModalManager = new VideoModalManager()
    globalVideoModalManager.init()
  }
})

// Export for potential external use
export { VideoModal, VideoModalManager }
export default VideoModalManager
